import React from 'react'
import { useTranslation } from 'react-i18next'
import { Package, CheckCircle2, Clock, Truck, MapPin, ChefHat } from 'lucide-react'

interface OrderStatusTimelineProps {
  status: string
  isDelivery?: boolean
  deliveryStatus?: string | null
  hasDelivery?: boolean
}

export function OrderStatusTimeline({ status, isDelivery = false, deliveryStatus = null, hasDelivery = false }: OrderStatusTimelineProps) {
  const { i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'

  // Unified timeline - show all 6 steps for delivery orders, only first 4 for pickup/dine-in
  const allStatuses = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered']
  const pickupStatuses = ['pending', 'confirmed', 'preparing', 'ready']
  
  const statuses = hasDelivery ? allStatuses : pickupStatuses

  const getStatusInfo = (s: string) => {
    const enInfo: Record<string, { title: string; description: string; icon: React.ReactNode }> = {
      pending: { 
        title: 'Order Placed', 
        description: 'We have received your order.',
        icon: <Package className="w-4 h-4" />
      },
      confirmed: { 
        title: 'Order Confirmed', 
        description: 'Your order has been confirmed.',
        icon: <CheckCircle2 className="w-4 h-4" />
      },
      preparing: { 
        title: 'Preparing', 
        description: 'We are preparing your order.',
        icon: <ChefHat className="w-4 h-4" />
      },
      ready: { 
        title: 'Ready', 
        description: 'Your order is ready.',
        icon: <Package className="w-4 h-4" />
      },
      out_for_delivery: { 
        title: 'Out for Delivery', 
        description: 'Your order is on the way.',
        icon: <Truck className="w-4 h-4" />
      },
      delivered: { 
        title: 'Delivered', 
        description: 'Order completed successfully.',
        icon: <CheckCircle2 className="w-4 h-4" />
      },
      completed: { 
        title: 'Completed', 
        description: 'Order completed successfully.',
        icon: <CheckCircle2 className="w-4 h-4" />
      }
    }

    const arInfo: Record<string, { title: string; description: string; icon: React.ReactNode }> = {
      pending: { 
        title: 'تم استقبال الطلب', 
        description: 'لقد استقبلنا طلبك.',
        icon: <Package className="w-4 h-4" />
      },
      confirmed: { 
        title: 'تم تأكيد الطلب', 
        description: 'تم تأكيد طلبك.',
        icon: <CheckCircle2 className="w-4 h-4" />
      },
      preparing: { 
        title: 'جاري التحضير', 
        description: 'نحن نحضر طلبك.',
        icon: <ChefHat className="w-4 h-4" />
      },
      ready: { 
        title: 'جاهز', 
        description: 'طلبك جاهز.',
        icon: <Package className="w-4 h-4" />
      },
      out_for_delivery: { 
        title: 'في الطريق', 
        description: 'طلبك في الطريق للتوصيل.',
        icon: <Truck className="w-4 h-4" />
      },
      delivered: { 
        title: 'تم التسليم', 
        description: 'تم إكمال الطلب بنجاح.',
        icon: <CheckCircle2 className="w-4 h-4" />
      },
      completed: { 
        title: 'مكتمل', 
        description: 'تم إكمال الطلب بنجاح.',
        icon: <CheckCircle2 className="w-4 h-4" />
      }
    }

    return isArabic ? arInfo[s] : enInfo[s]
  }

  // Map order status and delivery status to unified timeline position
  const getCurrentIndex = () => {
    // Map delivery statuses to unified timeline positions (only for delivery orders)
    if (hasDelivery && deliveryStatus) {
      const deliveryMap: Record<string, number> = {
        'assigned': 4,      // out_for_delivery
        'accepted': 4,      // out_for_delivery
        'picked_up': 4,     // out_for_delivery
        'in_transit': 4,    // out_for_delivery
        'delivered': 5,     // delivered
        'cancelled': -1
      }
      if (deliveryMap[deliveryStatus] !== undefined) {
        return deliveryMap[deliveryStatus]
      }
    }

    // Map order statuses to unified timeline positions
    const orderMap: Record<string, number> = {
      'pending': 0,
      'confirmed': 1,
      'preparing': 2,
      'ready': 3,
      'completed': hasDelivery ? 5 : 3, // For non-delivery, completed = ready
      'cancelled': -1
    }

    return orderMap[status] !== undefined ? orderMap[status] : 0
  }

  const currentIndex = getCurrentIndex()

  return (
    <div className="w-full">
      {/* Vertical Timeline - Compact */}
      <div className="space-y-0">
        {statuses.map((s, idx) => {
          const isCompleted = currentIndex >= idx
          const isCurrent = currentIndex === idx
          const info = getStatusInfo(s)

          return (
            <div key={s} className="relative flex gap-2 py-1">
              {/* Left Column - Timeline */}
              <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
                {/* Circle Point */}
                <div
                  className={`
                    w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 z-10
                    ${isCurrent 
                      ? 'bg-blue-500 border-2 border-blue-600 shadow-md' 
                      : isCompleted
                        ? 'bg-green-500 border-2 border-green-600'
                        : 'bg-gray-300 border-2 border-gray-400'
                    }
                  `}
                >
                  <div className="text-white">
                    {React.cloneElement(info.icon as React.ReactElement, {
                      className: 'w-3.5 h-3.5'
                    })}
                  </div>
                </div>

                {/* Vertical Line */}
                {idx < statuses.length - 1 && (
                  <div
                    className="w-1 transition-all duration-300"
                    style={{
                      backgroundColor: isCompleted && currentIndex > idx ? '#10b981' : '#e5e7eb',
                      height: '20px'
                    }}
                  />
                )}
              </div>

              {/* Right Column - Content */}
              <div className={`flex-1 ${isArabic ? 'pr-0.5' : 'pl-0.5'}`}>
                <div className={`${isCompleted ? 'opacity-100' : 'opacity-60'}`}>
                  <h3 className={`font-semibold transition-all duration-300 text-xs leading-tight ${
                    isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {info.title}
                  </h3>
                  <p className={`text-xs mt-0.5 transition-all duration-300 leading-tight ${
                    isCompleted ? 'text-gray-600' : 'text-gray-400'
                  }`}>
                    {info.description}
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

